// Documented icon-standard EXCEPTIONS: these two rows have no fitting
// conceptIcons/actionIcons/healthIcons glyph (see the per-row comments
// at "Status page" and "Simulator"), so they keep the raw lucide icon.
import { Activity as ActivityIcon, type LucideIcon, PlayCircle as PlayCircleIcon } from "lucide-react";

import { isDesignShowcaseUnlocked } from "@rook/shared/lib/designPreview";
import { conceptIcons } from "@rook/ui";


/**
 * Sidebar navigation config — single source of truth for the
 * portal's nav shape. Mirrors the legacy
 * `src/modules/home/utils/navigationOptions.tsx` order and grouping
 * (commit `b93f7377` — "restore Status page route + bundle Logs
 * under Monitoring group"), reshaped to the cleaner
 * discriminated-union types we want for v3:
 *
 *   FlatNavItem      — single nav row with its own path
 *   ExpandableNavItem — group header that expands to children
 *
 * Icons are Lucide React (`lucide-react`), one per row.
 *
 * MDI → lucide mapping (legacy iconify name in comments next to
 * each row). Where legacy mdi names did not have a perfect lucide
 * equivalent, picked the closest semantically — preferring shape
 * over literal-name match. The lucide set is the shadcn standard
 * and tree-shakes per-icon.
 *
 * `regex` is the matcher react-router's `useLocation()` checks
 * against. Important for parent-active highlighting (the parent
 * row stays "active" when a child route is open). Group-level
 * `matchRegex` covers ALL legacy URLs the group bundles, so
 * customer bookmarks landing on `/home/logs` still light up
 * "Monitoring".
 *
 * Net v3 deltas vs legacy:
 *   + `Account` flat row (settings/preferences — was inline in
 *     legacy Settings expandable; v3 lifts it to a top-level item)
 *   - `Documentation` external link removed from the sidebar
 *     (already linked from Topbar in v3)
 */

export type NavBadge = "beta" | "soon" | "enterprise";

export type FlatNavItem = {
  kind: "flat";
  title: string;
  path: string;
  regex: RegExp;
  Icon: LucideIcon;
  badge?: NavBadge;
  /**
   * Stable selector hook for the onboarding tour (`data-tour="…"`).
   * Optional — only the items the tour spotlights need to set it.
   * See `features/onboarding/sidebarTour.ts` for the consumed
   * selector names.
   */
  dataTour?: string;
};

export type ChildNavItem = {
  title: string;
  path: string;
  regex: RegExp;
  Icon: LucideIcon;
  badge?: NavBadge;
};

export type ExpandableNavItem = {
  kind: "expandable";
  title: string;
  Icon: LucideIcon;
  defaultPath: string;
  matchRegex: RegExp;
  children: ChildNavItem[];
  /** Tour selector hook — see FlatNavItem.dataTour. */
  dataTour?: string;
};

export type NavItem = FlatNavItem | ExpandableNavItem;

export const navItems: NavItem[] = [
  // mdi--view-dashboard-outline → LayoutDashboard (literal "dashboard" glyph)
  {
    kind: "flat",
    title: "Dashboard",
    path: "/home",
    regex: /^\/home\/?$/,
    Icon: conceptIcons.dashboard,
    dataTour: "nav-dashboard",
  },
  // mdi--account-group-outline → Users (direct match)
  {
    kind: "flat",
    title: "Users",
    path: "/home/users",
    regex: /^\/home\/users/,
    Icon: conceptIcons.user,
    dataTour: "nav-users",
  },
  // mdi--monitor-eye → MonitorDot (monitor with status indicator —
  // closer to "is the system working?" than plain Monitor).
  // Group covers /home/logs + /home/status-page so legacy bookmarks
  // light the parent row.
  {
    kind: "expandable",
    title: "Monitoring",
    Icon: conceptIcons.monitoring,
    defaultPath: "/home/logs",
    matchRegex: /^\/home\/(logs|status-page)/,
    dataTour: "nav-monitoring",
    children: [
      // mdi--receipt-text-outline → FileJson (per-document delivery
      // trail; the row shows JSON payloads in v3, so FileJson reads
      // truer than a receipt glyph)
      {
        title: "Logs",
        path: "/home/logs",
        regex: /^\/home\/logs/,
        Icon: conceptIcons.logs,
      },
      // mdi--pulse → Activity (1:1 — the "pulse line" glyph).
      // EXCEPTION: no conceptIcons/actionIcons/healthIcons glyph maps
      // to a "system status page". The nearest (healthIcons.heart_rate
      // = HeartPulse) reads as wearable heart-rate data, not uptime —
      // so the literal Activity pulse-line stays as a documented escape.
      {
        title: "Status page",
        path: "/home/status-page",
        regex: /^\/home\/status-page/,
        Icon: ActivityIcon,
      },
    ],
  },
  // mdi--shield-check-outline → ShieldCheck (1:1).
  // Group bundles Data Sources (was a sibling flat row in early v3)
  // + API + SDK — matches the legacy "everything that authenticates
  // a request" mental model.
  {
    kind: "expandable",
    title: "Auth",
    Icon: conceptIcons.authorization,
    defaultPath: "/home/auth/api-keys",
    matchRegex: /^\/home\/(auth|data-sources)/,
    dataTour: "nav-auth",
    children: [
      // mdi--watch-variant → Watch (wearable glyph — Data Sources
      // = which wearable providers the customer accepts)
      {
        title: "Data Sources",
        path: "/home/data-sources",
        regex: /^\/home\/data-sources/,
        Icon: conceptIcons.data_source,
        badge: "enterprise",
      },
      // mdi--api → KeyRound (lucide has no `api` glyph; KeyRound is
      // what we use everywhere else for credential surfaces)
      {
        title: "API",
        path: "/home/auth/api-keys",
        regex: /^\/home\/auth\/(api-keys|redirect)/,
        Icon: conceptIcons.credential,
      },
      // mdi--cube-outline → Box (SDK = packaged module; Box reads
      // truer than Cable for "shipped code module")
      {
        title: "SDK",
        path: "/home/auth/sdk",
        regex: /^\/home\/auth\/sdk/,
        Icon: conceptIcons.sdk,
      },
    ],
  },
  // mdi--watch-export-variant → Smartphone (extraction surface = the
  // device; phones cover both wearable-companion-app + standalone
  // phone health data, which is more customer-recognizable than a
  // watch glyph)
  {
    kind: "expandable",
    title: "Extraction",
    Icon: conceptIcons.extracted_data,
    defaultPath: "/home/extraction/timezone",
    matchRegex: /^\/home\/extraction/,
    dataTour: "nav-extraction",
    children: [
      // mdi--clock-outline → Clock (1:1)
      {
        title: "Timezone",
        path: "/home/extraction/timezone",
        regex: /^\/home\/extraction\/timezone/,
        Icon: conceptIcons.timezone,
      },
      // mdi--history → History (1:1)
      {
        title: "Pre-existing Data",
        path: "/home/extraction/pre-existing",
        regex: /^\/home\/extraction\/pre-existing/,
        Icon: conceptIcons.pre_existing_data,
      },
      // Retries — intentionally NOT rendered in the sidebar.
      // Pepe asked to hide the row on 2026-05-21: a `soon`-badged
      // row promises a roadmap surface, and we don't want to make
      // that promise until the team has scoped the work. The
      // /home/extraction/retries route + RetriesView still exist
      // so re-enabling later is a one-line change here.
    ],
  },
  // mdi--send-outline → Send (group-level glyph for "deliver data
  // OUT"; the legacy mdi name is literally "send")
  {
    kind: "expandable",
    title: "Delivery",
    Icon: conceptIcons.delivery,
    defaultPath: "/home/delivery/webhooks",
    matchRegex: /^\/home\/delivery/,
    dataTour: "nav-delivery",
    children: [
      // mdi--webhook → Webhook (1:1)
      {
        title: "Webhooks",
        path: "/home/delivery/webhooks",
        regex: /^\/home\/delivery\/webhooks/,
        Icon: conceptIcons.webhook,
      },
      // mdi--code-json → conceptIcons.data_structure (Braces — the
      // canonical "customize JSON keys" concept; same `{ }` glyph the
      // direct lucide gave us, now sourced from the concept set).
      {
        title: "Data Structure",
        path: "/home/delivery/data-structure",
        regex: /^\/home\/delivery\/data-structure/,
        Icon: conceptIcons.data_structure,
      },
      // mdi--chart-timeline-variant → conceptIcons.granular_data
      // (per-sample-row delivery is exactly the "granular data"
      // concept; the ChartScatter glyph reads as granular points).
      {
        title: "Granular",
        path: "/home/delivery/granular",
        regex: /^\/home\/delivery\/granular/,
        Icon: conceptIcons.granular_data,
        badge: "enterprise",
      },
      // mdi--code-braces → conceptIcons.non_structured_data (the raw
      // upstream blob IS the "non-structured data" concept; prefer the
      // concept over a literal file glyph per the icon standard).
      {
        title: "Non-structured",
        path: "/home/delivery/non-structured",
        regex: /^\/home\/delivery\/non-structured/,
        Icon: conceptIcons.non_structured_data,
        badge: "enterprise",
      },
    ],
  },
  // mdi--gauge → Gauge (1:1). Position 7 (after Delivery) per
  // legacy — Score is a downstream feature on top of delivered
  // data, not a peer of monitoring/extraction.
  {
    kind: "flat",
    title: "ROOK Score",
    path: "/home/score",
    regex: /^\/home\/score/,
    Icon: conceptIcons.score,
    badge: "enterprise",
    dataTour: "nav-score",
  },
  // mdi--flask-outline → conceptIcons.simulator (FlaskConical — the
  // "lab beaker"; Develop = sandbox tooling, and the simulator concept
  // owns this glyph in the design system).
  {
    kind: "expandable",
    title: "Develop",
    Icon: conceptIcons.simulator,
    defaultPath: "/home/develop/simulator",
    matchRegex: /^\/home\/develop/,
    dataTour: "nav-develop",
    children: [
      // mdi--script-text-play-outline → PlayCircle (the simulator
      // fires a synthetic webhook on click — Play is what the primary
      // CTA literally does).
      // EXCEPTION: no concept/action/health glyph is a "play" control,
      // and conceptIcons.simulator (FlaskConical) is already the parent
      // Develop group's icon — reusing it for the child would collapse
      // the visual hierarchy. Keep the literal PlayCircle.
      {
        title: "Simulator",
        path: "/home/develop/simulator",
        regex: /^\/home\/develop\/simulator/,
        Icon: PlayCircleIcon,
      },
      // mdi--web → Globe (1:1 — the page is a hosted public URL
      // customers share with end users to test connection flow)
      {
        title: "Connection Page",
        path: "/home/develop/connection-page",
        regex: /^\/home\/develop\/connection-page/,
        Icon: conceptIcons.connection_page,
      },
    ],
  },
  // v3-only — Account/Settings as its own flat row. Legacy nested
  // this inside a Settings expandable; v3 lifts it because we only
  // have one account surface (env switcher + plan + sign-out), so
  // a group header was overhead. Settings cog matches every other
  // SaaS sidebar convention.
  {
    kind: "flat",
    title: "Account",
    path: "/home/account",
    regex: /^\/home\/account/,
    Icon: conceptIcons.settings,
  },
];

/**
 * Showcase-only nav items (WP7), surfaced ONLY when the ?designShowcase
 * unlock is set — customers on a normal build never see them, so the
 * default `navItems` (and navigation.test) stay unchanged.
 */
export const BILLING_NAV_ITEM: FlatNavItem = {
  kind: "flat",
  title: "Billing",
  path: "/home/billing",
  regex: /^\/home\/billing/,
  Icon: conceptIcons.billing,
};

/**
 * The nav the sidebar renders. Default = `navItems`; when the showcase
 * is unlocked, Billing is inserted right after ROOK Score.
 */
export const getNavItems = (
  showcase: boolean = isDesignShowcaseUnlocked(),
): NavItem[] => {
  if (!showcase) return navItems;
  const items = [...navItems];
  const scoreIndex = items.findIndex(
    (item) => item.kind === "flat" && item.title === "ROOK Score",
  );
  const insertAt = scoreIndex >= 0 ? scoreIndex + 1 : items.length;
  items.splice(insertAt, 0, BILLING_NAV_ITEM);
  return items;
};

// Re-exported convenience for other modules that just want a
// flat list of "is this path a /home/* path?" matchers.
export const isHomePath = (pathname: string): boolean =>
  pathname === "/home" || pathname.startsWith("/home/");
